import {
  Material,
  MaterialGroup,
  MaterialTransaction,
  MaterialStock,
  CreateMaterialRequest,
  UpdateMaterialRequest,
  MaterialFilters,
  MaterialListResponse,
  MaterialDetailResponse,
  MaterialStatsResponse,
  ExportMaterialOptions
} from '@/types/material';
import { api } from '@/api/axios';

// Transform snake_case API response to camelCase Material interface
const transformMaterialResponse = (apiData: any): Material => {
  // Debug logging
  console.log('🔍 Transform API Data:', {
    code: apiData.code,
    actualQuantity: apiData.actualQuantity,
    actual_quantity: apiData.actual_quantity,
    availableQuantity: apiData.availableQuantity,
    available_quantity: apiData.available_quantity
  });

  // Calculate stock status if not provided
  const calculateStockStatus = () => {
    if (apiData.stockStatus || apiData.stock_status) return apiData.stockStatus || apiData.stock_status;

    const available = apiData.availableQuantity || apiData.available_quantity || 0;
    const minimum = apiData.minimumStock || apiData.minimum_stock || 0;

    if (available <= 0) return 'out_of_stock';
    if (available <= minimum) return 'low_stock';
    return 'in_stock';
  };

  const result = {
    id: apiData.id || '',
    code: apiData.code || '',
    name: apiData.name || '',
    categoryId: apiData.categoryId || apiData.category_id || '',
    categoryName: apiData.categoryName || apiData.category_name || 'Category Name', // Placeholder
    categoryGroupId: apiData.categoryGroupId || apiData.category_group_id || '',
    categoryGroupName: apiData.categoryGroupName || apiData.category_group_name || null, // Will show as '-' in UI
    unit: apiData.unit || '',
    supplierIds: apiData.supplierIds || apiData.supplier_ids || [],
    supplierNames: apiData.supplierNames || apiData.supplier_names || [],
    supplierAddresses: apiData.supplierAddresses || apiData.supplier_addresses || [],
    standardPrice: apiData.standardPrice || apiData.standard_price || 0,
    actualQuantity: apiData.actualQuantity || apiData.actual_quantity || 0,
    availableQuantity: apiData.availableQuantity || apiData.available_quantity || 0,
    holdQuantity: apiData.holdQuantity || apiData.hold_quantity || 0,
    shortageQuantity: apiData.shortageQuantity || apiData.shortage_quantity || 0,
    minimumStock: apiData.minimumStock || apiData.minimum_stock || 0,
    notes: apiData.notes || '',
    status: apiData.status || 'active',
    type: apiData.type || 'raw',
    cutting_type: apiData.cutting_type || 0,
    cutting_size_id: apiData.cutting_size_id || '',
    createdBy: apiData.createdBy || apiData.created_by || '',
    createdAt: apiData.createdAt || apiData.created_at || new Date().toISOString(),
    updatedAt: apiData.updatedAt || apiData.updated_at || new Date().toISOString(),
    stockStatus: apiData.stockStatus || calculateStockStatus(),
    lastTransactionDate: apiData.lastTransactionDate || apiData.last_transaction_date || null,
  };

  // Debug logging result
  console.log('✅ Transformed Material:', {
    code: result.code,
    actualQuantity: result.actualQuantity,
    availableQuantity: result.availableQuantity
  });

  return result;
};

// Helper function to build query parameters for materials
const buildMaterialQueryParams = (
  page: number = 1,
  pageSize: number = 10,
  filters?: MaterialFilters
): URLSearchParams => {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (filters?.search) {
    params.append('search', filters.search);
  }

  if (filters?.status && filters.status.length > 0) {
    // Backend expects single status parameter, use first status for now
    params.append('status', filters.status[0]);
  }

  if (filters?.stockStatus && filters.stockStatus.length > 0) {
    // Backend expects single stock_status parameter
    params.append('stock_status', filters.stockStatus[0]);
  }

  if (filters?.categoryId) {
    params.append('category_id', filters.categoryId);
  }

  if (filters?.supplierId) {
    params.append('supplier_id', filters.supplierId);
  }

  if (filters?.type && filters.type.length > 0) {
    // Support multiple material types by sending comma-separated values
    // Example: type=raw,semi-product
    params.append('type', filters.type.join(','));
  }

  if (filters?.availableOnly) {
    params.append('available_only', 'true');
  }

  console.log('buildMaterialQueryParams - Final params:', params.toString());
  return params;
};

export const materialApi = {
  // Get materials list with filters and pagination
  getMaterials: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: MaterialFilters
  ): Promise<MaterialListResponse> => {
    try {
      const params = buildMaterialQueryParams(page, pageSize, filters);
      // Add cache busting parameter
      params.append('_t', Date.now().toString());
      const response = await api.get(`/materials?${params}`);

      // Extract data from StandardResponse wrapper
      return {
        data: (response.data.data || []).map(transformMaterialResponse),
        total: response.data.total || 0,
        page: response.data.page || page,
        pageSize: response.data.pageSize || pageSize,
      };
    } catch (error: any) {
      console.error('Error fetching materials:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch materials'
      );
    }
  },

  // Get single material by code
  getMaterial: async (code: string): Promise<Material> => {
    try {
      const response = await api.get(`/materials/${code}`);
      return transformMaterialResponse(response.data.data);
    } catch (error: any) {
      console.error(`Error fetching material ${code}:`, error);
      if (error.response?.status === 404) {
        throw new Error(`Material with code ${code} not found`);
      }
      throw new Error(
        error.response?.data?.message || 'Failed to fetch material'
      );
    }
  },

  // Get materials by codes (bulk lookup)
  getMaterialsByCodes: async (codes: string[]): Promise<Material[]> => {
    try {
      if (codes.length === 0) {
        return [];
      }
      const response = await api.post<{ data: Record<string, any> }>('/materials/by-codes', { codes });
      // Backend returns a map { [code: string]: MaterialResponse }
      // Convert to array
      const materialsMap = response.data.data || {};
      return Object.values(materialsMap).map(transformMaterialResponse);
    } catch (error: any) {
      console.error('Error fetching materials by codes:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch materials by codes'
      );
    }
  },

  // Get material detail with transactions and stock
  getMaterialDetail: async (code: string): Promise<MaterialDetailResponse> => {
    try {
      const materialResponse = await api.get(`/materials/${code}`);
      const material = transformMaterialResponse(materialResponse.data.data);

      // Get stock history
      let transactions: MaterialTransaction[] = [];
      let stockRecords: MaterialStock[] = [];
      let totalTransactions = 0;

      try {
        const historyResponse = await api.get(`/materials/${code}/stock/history`);
        transactions = historyResponse.data.data || [];
        totalTransactions = historyResponse.data.total || 0;
      } catch (historyError: any) {
        console.warn('Could not fetch stock history:', historyError);

        // Check if it's the "please update api" error
        if (historyError.response?.data?.message?.includes('please update api')) {
          console.info('Stock history endpoint not yet implemented on backend');
        }

        // For now, we'll leave transactions empty
        // This will be populated when the backend implements the endpoint
        transactions = [];
        totalTransactions = 0;
      }

      // For now, stockRecords will be empty since there's no specific endpoint
      // This can be enhanced when the backend provides stock records endpoint

      return {
        material,
        transactions,
        stockRecords,
        totalTransactions,
      };
    } catch (error: any) {
      console.error(`Error fetching material detail ${code}:`, error);
      if (error.response?.status === 404) {
        throw new Error(`Material with code ${code} not found`);
      }
      throw new Error(
        error.response?.data?.message || 'Failed to fetch material details'
      );
    }
  },

  // Create new material
  createMaterial: async (data: CreateMaterialRequest): Promise<Material> => {
    try {
      const payload: any = {
        name: data.name,
        categoryId: data.categoryId, // backend expects categoryId string
        supplierIds: [], // Intentionally empty - supplier association is handled separately from material creation
        unit: data.unit,
        standardPrice: data.standardPrice,
        minimumStock: data.minimumStock,
        notes: data.notes || '',
        status: data.status,
        // defaults required by backend schema
        actualQuantity: data.actualQuantity ?? data.quantity ?? 0,
        availableQuantity: data.actualQuantity ?? data.quantity ?? 0,
        holdQuantity: 0,
        shortageQuantity: 0,
        // optional group mapping
        categoryGroupId: data.categoryGroupId || data.groupId || '',
        // material type default
        type: 'raw',
        cutting_type: 0,
        cutting_size_id: '',
      };
      // Clean empty optional fields to avoid validation errors
      if (!payload.categoryGroupId) delete payload.categoryGroupId;
      if (!payload.notes) delete payload.notes;
      if (!payload.cutting_size_id) delete payload.cutting_size_id;
      const response = await api.post('/materials', payload);
      return response.data.data;
    } catch (error: any) {
      console.error('Error creating material:', error?.response?.data || error);
      if (error.response?.status === 422) {
        const details = error.response?.data?.details;
        const message = error.response?.data?.message || 'Validation error';
        const prettyDetails = details ? JSON.stringify(details) : '';
        throw new Error(`${message}${prettyDetails ? `: ${prettyDetails}` : ''}`);
      }
      throw new Error(
        error.response?.data?.message || 'Failed to create material'
      );
    }
  },

  // Update material
  updateMaterial: async (data: UpdateMaterialRequest & { code: string }): Promise<Material> => {
    try {
      const { code, ...updateData } = data;
      const response = await api.put(`/materials/${code}`, updateData);
      return response.data.data;
    } catch (error: any) {
      console.error(`Error updating material ${data.code}:`, error);
      if (error.response?.status === 404) {
        throw new Error(`Material with code ${data.code} not found`);
      }
      if (error.response?.status === 422) {
        throw new Error(
          error.response?.data?.message || 'Validation error'
        );
      }
      throw new Error(
        error.response?.data?.message || 'Failed to update material'
      );
    }
  },

  // Delete material
  deleteMaterial: async (code: string): Promise<void> => {
    try {
      await api.delete(`/materials/${code}`);
    } catch (error: any) {
      console.error(`Error deleting material ${code}:`, error);
      if (error.response?.status === 404) {
        throw new Error(`Material with code ${code} not found`);
      }
      if (error.response?.status === 409) {
        throw new Error('Cannot delete material with existing transactions');
      }
      if (error.response?.status === 601) {
        // Custom status code for material in use by service line
        const errorMessage = error.response?.data?.message || 'Material is in use by service line';
        const customError: any = new Error(errorMessage);
        customError.statusCode = 601;
        throw customError;
      }
      throw new Error(
        error.response?.data?.message || 'Failed to delete material'
      );
    }
  },

  // Get material groups
  getMaterialGroups: async (_categoryId?: string): Promise<MaterialGroup[]> => {
    try {
      // This endpoint doesn't exist yet, so we'll implement a placeholder
      // that throws an error indicating it's not implemented
      throw new Error('Material groups endpoint not yet implemented on backend');
    } catch (error: any) {
      console.error('Error fetching material groups:', error);
      throw new Error(
        error.response?.data?.message || 'Material groups not available'
      );
    }
  },

  // Get material transactions
  getMaterialTransactions: async (
    materialCode: string,
    page: number = 1,
    pageSize: number = 10
  ) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      const response = await api.get(`/materials/${materialCode}/stock/history?${params}`);
      return {
        data: response.data.data || [],
        total: response.data.total || 0,
        page: response.data.page || page,
        pageSize: response.data.pageSize || pageSize,
      };
    } catch (error: any) {
      console.error(`Error fetching transactions for material ${materialCode}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch material transactions'
      );
    }
  },

  // Get material stock records
  getMaterialStocks: async (
    materialCode: string,
    page: number = 1,
    pageSize: number = 10
  ) => {
    try {
      // This endpoint might not exist yet, so we'll use the transactions endpoint
      // and filter for stock-related transactions
      return await materialApi.getMaterialTransactions(materialCode, page, pageSize);
    } catch (error: any) {
      console.error(`Error fetching stock records for material ${materialCode}:`, error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch material stock records'
      );
    }
  },

  // Export material data
  exportMaterialData: async (_options: ExportMaterialOptions): Promise<Blob> => {
    try {
      // This endpoint doesn't exist yet, so we'll implement a placeholder
      throw new Error('Export functionality not yet implemented on backend');
    } catch (error: any) {
      console.error('Error exporting material data:', error);
      throw new Error(
        error.response?.data?.message || 'Export functionality not available'
      );
    }
  },

  // Get material statistics
  getMaterialStats: async (): Promise<MaterialStatsResponse> => {
    try {
      // Use the new count-by-status endpoint for efficient counting
      const countResponse = await api.get('/materials/count-by-status');
      const counts = countResponse.data.data || { all: 0, active: 0, inactive: 0 };

      // For other stats like low stock, we still need to fetch materials
      // but we can use a smaller page size since we're only counting
      const response = await api.get('/materials?page=1&page_size=1000');
      const materials = (response.data.data || []).map(transformMaterialResponse);

      const totalValue = materials.reduce((sum: number, m: Material) => sum + (m.standardPrice * m.actualQuantity), 0);
      const activeMaterials = materials.filter((m: Material) => m.status === 'active');

      return {
        totalMaterials: counts.all,
        activeMaterials: counts.active,
        inactiveMaterials: counts.inactive,
        lowStockMaterials: materials.filter((m: Material) => m.stockStatus === 'low_stock').length,
        outOfStockMaterials: materials.filter((m: Material) => m.stockStatus === 'out_of_stock').length,
        totalValue,
        averagePrice: activeMaterials.length > 0 ?
          activeMaterials.reduce((sum: number, m: Material) => sum + m.standardPrice, 0) / activeMaterials.length : 0,
      };
    } catch (error: any) {
      console.error('Error fetching material statistics:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch material statistics'
      );
    }
  },

  // Get material count by status (lightweight endpoint)
  getMaterialCountByStatus: async (): Promise<{ all: number; active: number; inactive: number }> => {
    try {
      const response = await api.get('/materials/count-by-status');
      return response.data.data || { all: 0, active: 0, inactive: 0 };
    } catch (error: any) {
      console.error('Error fetching material count by status:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch material count by status'
      );
    }
  },

  // Import materials from file
  importMaterials: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/materials/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Export materials to file
  exportMaterials: async (format: 'csv' | 'xlsx', filters?: MaterialFilters): Promise<Blob> => {
    const params: Record<string, any> = { format };

    // Add filters to export params
    if (filters?.search) {
      params.search = filters.search;
    }
    if (filters?.categoryId) {
      params.categoryIds = filters.categoryId;
    }
    if (filters?.status && filters.status.length > 0) {
      params.status = filters.status.join(',');
    }
    if (filters?.stockStatus && filters.stockStatus.length > 0) {
      params.stockStatus = filters.stockStatus.join(',');
    }

    const queryStr = new URLSearchParams(params).toString();
    const response = await api.get(`/materials/export?${queryStr}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get import template
  getMaterialImportTemplate: async (format: 'csv' | 'xlsx'): Promise<Blob> => {
    const response = await api.get(`/materials/import/template?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};
